import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { Toaster } from '@/components/ui/toaster'

// Layouts
import AuthLayout from '@/layouts/AuthLayout'
import AppLayout from '@/layouts/AppLayout'
import ProjectLayout from '@/layouts/ProjectLayout'

// Auth pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import AuthCallbackPage from '@/pages/auth/AuthCallbackPage'
import AcceptInvitePage from '@/pages/auth/AcceptInvitePage'

// App pages
import DashboardPage from '@/pages/DashboardPage'
import ScriptEditorPage from '@/pages/ScriptEditorPage'
import BeatBoardPage from '@/pages/BeatBoardPage'
import DraftEditorPage from '@/pages/DraftEditorPage'
import TeamPage from '@/pages/TeamPage'
import SettingsPage from '@/pages/SettingsPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuthStore()

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        )
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />
    }

    return <>{children}</>
}

function PublicRoute({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, isLoading } = useAuthStore()

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
        )
    }

    if (isAuthenticated) {
        return <Navigate to="/" replace />
    }

    return <>{children}</>
}

export default function App() {
    return (
        <>
            <Routes>
                {/* Public auth routes */}
                <Route element={<AuthLayout />}>
                    <Route
                        path="/login"
                        element={
                            <PublicRoute>
                                <LoginPage />
                            </PublicRoute>
                        }
                    />
                    <Route
                        path="/register"
                        element={
                            <PublicRoute>
                                <RegisterPage />
                            </PublicRoute>
                        }
                    />
                    <Route path="/auth/callback" element={<AuthCallbackPage />} />
                    <Route path="/invite/:token" element={<AcceptInvitePage />} />
                </Route>

                {/* Protected app routes - org level (dashboard, team, settings) */}
                <Route
                    element={
                        <ProtectedRoute>
                            <AppLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<DashboardPage />} />
                    <Route path="/team" element={<TeamPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                </Route>

                {/* Protected project routes - project context (uses ProjectLayout) */}
                <Route
                    element={
                        <ProtectedRoute>
                            <ProjectLayout />
                        </ProtectedRoute>
                    }
                >
                    {/* Default: redirect to edit view */}
                    <Route path="/projects/:projectId" element={<DraftEditorPage />} />

                    {/* Legacy script/beat board routes - for existing links */}
                    <Route path="/projects/:projectId/scripts/:scriptId" element={<ScriptEditorPage />} />
                    <Route path="/projects/:projectId/beat-boards/:beatBoardId" element={<BeatBoardPage />} />

                    {/* Draft editor routes */}
                    <Route path="/projects/:projectId/edit" element={<DraftEditorPage />} />
                    <Route path="/projects/:projectId/drafts/:draftId" element={<DraftEditorPage />} />
                </Route>

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster />
        </>
    )
}


